import { isFunction, isNumber } from "lodash";
import slugify from "slugify";
import type { AsyncReturnType } from "type-fest";
import { uid } from "uid";
import type {
  TimingDetails,
  TimingEvent,
  TimingSpan,
  TimingSpanWithEndTime,
  TimingSpanWithoutEndTime,
  TimingStrategy,
} from "../types";
import { NoopTimingStrategy } from "./NoopTimingStrategy";

/**
 * Implementation of timings.
 */
class TimingsImpl {
  private static RANDOM_SUFFIX_LENGTH = 8;

  private _strategies: TimingStrategy[] = [];

  public constructor(private readonly performanceApi: typeof performance = performance) {}

  private get strategies(): TimingStrategy[] {
    return this._strategies;
  }

  public setStrategies(strategies: TimingStrategy[]): void {
    this._strategies = strategies.length > 0 ? strategies : [new NoopTimingStrategy()];
  }

  public startSpan(name: string, details?: TimingDetails): TimingSpanWithoutEndTime {
    const span: TimingSpanWithoutEndTime = {
      id: this.idFromName(name),
      name,
      details,
      startTime: this.performanceApi.now(),
      endTime: undefined,
    };

    for (const strategy of this.strategies) {
      strategy.startSpan(span);
    }

    return span;
  }

  public endSpan(span: TimingSpanWithEndTime): void {
    for (const strategy of this.strategies) {
      strategy.endSpan(span);
    }
  }

  public createSpan(name: string, details?: TimingDetails): void;
  public createSpan(name: string, startTime: DOMHighResTimeStamp, details?: TimingDetails): void;
  public createSpan(
    name: string,
    startTime: DOMHighResTimeStamp,
    endTime: DOMHighResTimeStamp,
    details?: TimingDetails,
  ): void;
  public createSpan(
    name: string,
    startTimeOrDetails?: DOMHighResTimeStamp | TimingDetails,
    endTimeOrDetails?: DOMHighResTimeStamp | TimingDetails,
    details?: TimingDetails,
  ): void {
    const resolvedStartTime = isNumber(startTimeOrDetails) ? startTimeOrDetails : this.performanceApi.now();
    const resolvedEndTime = isNumber(endTimeOrDetails) ? endTimeOrDetails : this.performanceApi.now();
    const resolvedDetails = isNumber(startTimeOrDetails)
      ? isNumber(endTimeOrDetails)
        ? details
        : endTimeOrDetails
      : startTimeOrDetails;

    const span: TimingSpan = {
      id: this.idFromName(name),
      name,
      details: resolvedDetails,
      startTime: resolvedStartTime,
      endTime: resolvedEndTime,
    };

    for (const strategy of this.strategies) {
      strategy.createSpan(span);
    }
  }

  // biome-ignore lint/suspicious/noExplicitAny: Need to match ReturnType<F>, which uses any
  public async withSpan<F extends () => Promise<any>>(name: string, callback: F): Promise<AsyncReturnType<F>>;
  // biome-ignore lint/suspicious/noExplicitAny: Need to match ReturnType<F>, which uses any
  public async withSpan<F extends () => Promise<any>>(
    name: string,
    details: TimingDetails,
    callback: F,
  ): Promise<AsyncReturnType<F>>;
  // biome-ignore lint/suspicious/noExplicitAny: Need to match ReturnType<F>, which uses any
  public async withSpan<F extends () => Promise<any>>(
    name: string,
    detailsOrCallback: F | TimingDetails,
    callback?: F,
  ): Promise<AsyncReturnType<F>> {
    const resolvedCallback = isFunction(detailsOrCallback) ? detailsOrCallback : callback;

    if (!resolvedCallback) {
      throw new Error("Timings.withSpan invoked without callback");
    }

    const resolvedDetails = isFunction(detailsOrCallback) ? undefined : detailsOrCallback;

    const span: TimingSpanWithoutEndTime = Object.freeze({
      id: this.idFromName(name),
      name,
      details: resolvedDetails,
      startTime: this.performanceApi.now(),
      endTime: undefined,
    });

    return await this.strategies.reduceRight(
      (cb, strategy) => (async () => await strategy.withSpan(span, cb)) as F,
      resolvedCallback,
    )();
  }

  public createEvent(name: string, details?: TimingDetails): TimingEvent;
  public createEvent(name: string, startTime: DOMHighResTimeStamp, details?: TimingDetails): TimingEvent;
  public createEvent(
    name: string,
    detailsOrStartTime?: DOMHighResTimeStamp | TimingDetails,
    details?: TimingDetails,
  ): TimingEvent {
    const resolvedStartTime =
      detailsOrStartTime && isNumber(detailsOrStartTime) ? detailsOrStartTime : this.performanceApi.now();
    const resolvedDetails = detailsOrStartTime && !isNumber(detailsOrStartTime) ? detailsOrStartTime : details;

    const event = {
      id: this.idFromName(name),
      name,
      details: resolvedDetails,
      startTime: resolvedStartTime,
    };

    for (const strategy of this.strategies) {
      strategy.createEvent(event);
    }

    return event;
  }

  public reset(): void {
    for (const strategy of this.strategies) {
      strategy.reset();
    }
  }

  private idFromName(name: string): string {
    return `${slugify(name, { lower: true, strict: true })}-${uid(TimingsImpl.RANDOM_SUFFIX_LENGTH)}`;
  }
}

export const Timings = new TimingsImpl();
